import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { getUrl } from "aws-amplify/storage";
import { FileDown } from "lucide-react";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReportWrapper, { ReportType } from "../../abstract/Report";
import DefaultLogo from "../../assets/images/report-default-logo.png";
import CardWithHeader from "../../components/cards/CardWithHeader";
import HalfPieChart from "../../components/report/charts/HalfPieChart";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useReport } from "../../contexts/ReportContext";
import PDF from "../../pdf/PDF";
import {
  colors,
  fontSizes,
  ReportColorStyle,
  ReportColorStyleName,
} from "../../theme";
import {
  clampPercentage,
  convertImageToBase64,
  getAge,
  getBucketItemPath,
  getFileExtension,
  scoreColor,
  VIDEO_EXTENSIONS,
} from "../../utils";
import { ReportOverrideFooter } from "../EggFreezeCalculator/types";
import PatientInfoCard from "./PatientInfoCard";
import {
  FertilizationMethod,
  GetCycleResponse,
  GetCycleResponseItem,
} from "./types";
import Progress from "../../components/Progress";
import SubmitButton from "../../components/SubmitButton";
import Reviews, { ReviewItem, ReviewStage } from "../../components/Reviews";
import { formatDate } from "date-fns";

const ScoreCard: React.FC<{
  title: string;
  score: number | null;
  note?: string;
}> = ({ title, score, note }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        padding: theme.spacing(2),
        border: "1px solid #e0e0e0",
        borderRadius: 2,
      }}
    >
      <Stack
        gap={1}
        width={"100%"}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Stack gap={theme.spacing(0)}>
          <Typography fontWeight={"bold"} variant="report.info.label">
            {title}
          </Typography>
          {note && <Typography variant="caption">{note}</Typography>}
        </Stack>

        {score !== null && (
          <HalfPieChart
            width={120}
            height={60}
            labels={["", ""]}
            rate={score}
          />
        )}
      </Stack>
    </Card>
  );
};

const Report: React.FC<{ type: ReportType }> = ({ type }) => {
  const { cycleId } = useParams();
  const { apiService, setSubmitting } = useApi();
  const { showError, showSuccess } = useError();
  const { updateBreadcrumbs, updateActions } = useApp();
  const navigate = useNavigate();
  const { colorStyle, toggleEditMode, reportEditMode } = useReport();

  const [reviews, setReviews] = useState<ReviewItem[]>([
    {
      id: "1",
      stage: ReviewStage.transferOutcome,
    },
    {
      id: "2",
      stage: ReviewStage.liveBirth,
      liveBirths: 0,
      transferDate: null,
      successfulPregnancy: null,
    },
  ]);

  const { user } = useAuth();
  const theme = useTheme();
  const analysisRefs = useRef<RefObject<HTMLDivElement>[]>([]);
  const [contactDetails, setContactDetails] =
    useState<ReportOverrideFooter | null>(null);

  const [cycle, setCycle] = useState<GetCycleResponseItem | null>(null);
  const [mediaUrls, setMediaUrls] = useState<{ [id: string]: string }>({});
  const [selectedSections, setSelectedSections] = useState<{
    [key: string]: boolean;
  }>({
    embryoAnalysis: true,
  });

  const { translate } = useApp();
  const translations = useMemo(() => translate("embryo"), [translate]);

  useEffect(() => {
    const reviewsCopy = [...reviews];
    console.info("cycle", cycle);
    if (cycle?.u_transfer_date) {
      reviewsCopy[0] = {
        ...reviewsCopy[0],
        transferDate: new Date(cycle.u_transfer_date),
      } as ReviewItem;
    }
    if (cycle?.u_transfer_outcome) {
      reviewsCopy[1] = {
        ...reviewsCopy[1],
        successfulPregnancy: !!cycle.u_transfer_outcome,
      } as ReviewItem;
    }
    if (cycle?.u_livebirth) {
      reviewsCopy[1] = {
        ...reviewsCopy[1],
        liveBirths: cycle.u_livebirth,
      } as ReviewItem;
    }
    setReviews(reviewsCopy);
  }, [cycle]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
    if (user.config?.contact_details) {
      setContactDetails(user.config.contact_details);
    }
  }, [user]);

  useEffect(() => {
    apiService
      .get<GetCycleResponse>(`/embryo/${cycleId}`)
      .then((response) => {
        setCycle(response.cycle);
        loadMedia(response.cycle);
      })
      .catch((error) => {
        console.log("error", error);
        showError(translations.report.messages.errorFetchingCycleData);
      });
  }, [type]);

  useEffect(() => {
    const breadCrumbs = [
      {
        label: translations.breadcrumbs.embryoAssessment,
        onClick: () => navigate("/embryo", { replace: true }),
      },
      {
        label: translations.breadcrumbs.history,
        onClick: () => navigate(`/embryo/${cycleId}`, { replace: true }),
      },
    ];
    if (type === "report") {
      breadCrumbs.push({
        label: translations.breadcrumbs.report,
        onClick: () => {},
      });
    }
    updateBreadcrumbs(breadCrumbs);
  }, [type, cycleId]);

  useEffect(() => {
    if (cycle) {
      analysisRefs.current = cycle.images.map(() =>
        React.createRef<HTMLDivElement>()
      );
    }
  }, [cycle]);

  useEffect(() => {
    updateActions([
      <SubmitButton
        sx={{ width: 170 }}
        key={"export"}
        color={"blackButton"}
        onClick={exportToPDF}
      >
        <Stack direction={"row"} spacing={1}>
          <FileDown />
          <Typography variant="medium" fontSize={fontSizes.sm}>
            {translations.report.actions.exportPDF}
          </Typography>
        </Stack>
      </SubmitButton>,
    ]);

    return () => {
      updateActions([]);
    };
  }, [type, cycle]);

  const handleReviewSaved = async (review: any) => {
    const fields: any = {};
    const newReviews: any = [...reviews];
    console.log("review", review);
    if (review.stage === ReviewStage.transferOutcome) {
      newReviews[0] = {
        ...newReviews[0],
        outcome: review.outcome,
        transferDate: review.transferDate,
      };
    } else if (review.stage === ReviewStage.liveBirth) {
      newReviews[1] = {
        ...newReviews[1],
        ...review,
      };
    }
    if (newReviews[0].outcome) {
      fields["u_transfer_outcome"] = newReviews[0].outcome === "yes" ? 1 : 0;
    }
    if (newReviews[0].transferDate) {
      fields["u_transfer_date"] = formatDate(
        newReviews[0].transferDate,
        "yyyy-MM-dd"
      );
    }
    if (newReviews[1].oocytes) {
      fields["u_livebirth"] = newReviews[1].successfulPregnancy ? 1 : 0;
    }
    if (newReviews[1].liveBirths) {
      fields["u_overall"] = newReviews[1].liveBirths;
    }

    try {
      const response = await apiService.post(`/embryo/${cycleId}`, {
        fields,
      });
      if ((response as any).cycle?.[0]?.updated) {
        showSuccess(translations.report.messages.reviewDataSaved);
        console.log("newReviews", newReviews);
        setReviews(newReviews);
      } else {
        showError(translations.report.errors.errorSavingReviewData);
      }
    } catch (error) {
      console.log("error", error);
      showError(translations.report.errors.errorSavingReviewData);
      throw error;
    }
  };

  const fertalizationMethod = useMemo(() => {
    if (!cycle) {
      return "";
    }
    return cycle.fertilization === null
      ? ""
      : cycle.fertilization === 0
      ? "IVF"
      : "ICSI";
  }, [cycle]);

  const exportToPDF = useCallback(async () => {
    if (!cycle) {
      return;
    }
    const pdf = new PDF(
      theme.palette.reportColorStyles[
        colorStyle as ReportColorStyleName
      ] as ReportColorStyle,
      translations.report.title,
      contactDetails ?? undefined,
      800
    );

    setSubmitting(true);

    pdf.setDate(new Date(cycle.created!));
    const logoElm = document.getElementById("report-logo") as HTMLImageElement;
    const logoSrc = logoElm?.getAttribute("src") || DefaultLogo;
    convertImageToBase64(logoSrc)
      .then((b64) => {
        const originalWidth =
          logoElm?.naturalWidth ?? logoElm?.clientWidth ?? 64;
        const originalHeight =
          logoElm?.naturalHeight ?? logoElm?.clientHeight ?? 64;

        const logoWidth = Math.min(originalWidth, 64);
        const logoHeight = (logoWidth / originalWidth) * originalHeight;

        pdf.setLogo(b64, {
          width: logoWidth,
          height: logoHeight,
        });
      })
      .catch((error) => {
        console.error("Error converting logo to base64", error);
        showError(translations.report.messages.errorRetrievingLogo);
      });

    const rows = [
      {
        columns: [
          { key: translations.report.pdf.fields.name, value: cycle.patient },
        ],
      },
      {
        columns: [
          {
            key: translations.report.pdf.fields.scheduledTransferDate,
            value: `${cycle.transfer}`,
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.pdf.fields.fertilizationMethod,
            value:
              cycle.fertilization === FertilizationMethod.ICSI ? "ICSI" : "IVF",
          },
        ],
      },
      {
        columns: [
          {
            key: translations.report.pdf.fields.oocyteProviderAge,
            value: `${getAge(new Date(cycle.birth))}`,
          },
        ],
      },
    ];

    if (!cycle.transfer) {
      rows.splice(1, 1);
    }

    if (!fertalizationMethod) {
      rows.splice(2, 1);
    }

    pdf.setPatientInformation(
      {
        rows: rows,
      },
      0.5
    );

    pdf.build();

    const cycleAnalysis = document.getElementById("section-1");
    pdf.addHtmlCanvas(cycleAnalysis!);

    if (selectedSections.embryoAnalysis) {
      for (let index = 0; index < cycle.images.length; index++) {
        if (index % 2 === 1) {
          pdf.addPage();
        }
        const element = document.getElementById(`embryo-${index}`);
        if (element) {
          await pdf.addHtmlCanvas(element);
        }
      }
    }
    setSubmitting(false);
    pdf.save(`Embryo_Quality_Assessment_${user.clinic}.pdf`);
  }, [cycle, contactDetails, selectedSections]);

  const loadMedia = (cycle: GetCycleResponseItem) => {
    for (const image of cycle.images) {
      const origExt = getFileExtension(image.filename);
      const thumbExt = VIDEO_EXTENSIONS.includes(origExt) ? "jpg" : origExt;
      getUrl({
        path: getBucketItemPath(
          user.clinic,
          "embryo",
          cycle.id,
          image.id,
          thumbExt
        ),
      }).then((response) => {
        const imageUrl = response.url.href;

        convertImageToBase64(imageUrl).then((base64) => {
          setMediaUrls((prev) => ({
            ...prev,
            [image.id]: base64,
          }));
        });
      });
    }
  };

  if (!cycle) {
    return <Progress />;
  }

  return (
    <ReportWrapper
      type={type}
      title={translations.report.title}
      sections={[
        {
          title: translations.report.sections.patientDetails,
          component: (
            <PatientInfoCard cycle={cycle} onExpand={() => {}} type={type} />
          ),
        },
        {
          title: translations.report.sections.cycleAnalysis,
          component: (
            <Stack direction={"row"} spacing={2}>
              {cycle.p_overall !== null && (
                <CardWithHeader
                  width={"100%"}
                  title={translations.report.cards.overallScore}
                >
                  <Stack spacing={2}>
                    <Chip
                      sx={{
                        width: 60,
                        backgroundColor: scoreColor(cycle.p_overall),
                        fontWeight: "normal",
                        fontSize: fontSizes.lg,
                        color: colors.stone[900],
                      }}
                      label={`${cycle.p_overall}`}
                    />
                  </Stack>
                </CardWithHeader>
              )}

              {cycle.p_livebirth !== null && cycle.s_livebirth !== null && (
                <CardWithHeader
                  width={"100%"}
                  title={translations.report.cards.overallLiveBirthProbability}
                >
                  <Stack spacing={1}>
                    <Typography
                      variant="medium"
                      fontSize={fontSizes.lg}
                      gutterBottom
                    >
                      {clampPercentage(cycle.p_livebirth - cycle.s_livebirth)}%
                      - {clampPercentage(cycle.p_livebirth + cycle.s_livebirth)}
                      %
                    </Typography>

                    <Typography
                      variant="normal"
                      fontSize={fontSizes.lg}
                      color={colors.stone[500]}
                      gutterBottom
                    >
                      {translations.report.messages.liveBirthChance}
                    </Typography>
                  </Stack>
                </CardWithHeader>
              )}
            </Stack>
          ),
        },
        {
          title: translations.report.sections.embryoAnalysis,
          component: (
            <Stack gap={2} width={"100%"}>
              {cycle?.images.map((image, index) => (
                <Card
                  id={`embryo-${index}`}
                  key={index}
                  sx={{
                    padding: theme.spacing(3),
                    borderRadius: 2,
                    opacity: image.invalid ? 0.3 : 1,
                  }}
                >
                  <Stack gap={1} width={"100%"}>
                    <Typography
                      fontSize={fontSizes.lg}
                      variant="medium"
                      gutterBottom
                    >
                      {translations.report.labels.embryo} {index + 1}
                    </Typography>

                    <Stack direction={"row"} gap={2}>
                      <Box width={"50%"}>
                        {mediaUrls[image.id] && (
                          <img
                            src={mediaUrls[image.id]}
                            style={{ width: "100%" }}
                          />
                        )}
                      </Box>
                      <Box width={"50%"}>
                        <Stack gap={theme.spacing(2)}>
                          {image.p_implantation !== null && (
                            <ScoreCard
                              title={
                                translations.report.scoreCard
                                  .implantationProbability
                              }
                              score={image.p_implantation}
                            />
                          )}
                          {image.p_aneuploidy !== null && (
                            <ScoreCard
                              title={
                                translations.report.scoreCard
                                  .aneuploidyRiskEstimate
                              }
                              score={image.p_aneuploidy}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ),
        },
        {
          title: "",
          component: <Reviews onSave={handleReviewSaved} reviews={reviews} />,
        },
      ]}
    />
  );
};

export default Report;
